import { Link, useParams } from '@tanstack/react-router';
import { useSnapshot } from 'valtio';
import Icon from '~/components2/Icon';
import type { ProjectDetailsParams } from '~/pages/projectDetails';
import { CHANGE_ORDER_FEE } from '~/pages/projectDetails/details/editGeometry/store';
import store from '../../../../store';
import { DrawerContainer, DrawerContent, DrawerFooter } from '../../../Drawer';
import { NextButton, PreviousButton } from '../../StepButtons';

export default function Intro() {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { isFullUtilitiesExperienceEnabled } = useSnapshot(store);

  if (!isFullUtilitiesExperienceEnabled) {
    return (
      <DrawerContainer>
        <DrawerContent>
          <div>
            <div className="text-base">⚠️ CivilGrid Records Research is not included for your project.</div>
            <div className="my-4">
              <Link to="/projects/$projectId/$tabId" params={{ projectId, tabId: 'details' }}>
                Upgrade
              </Link>{' '}
              your project to have CivilGrid handle utility data requests on your behalf and manage the entire process
              from start to finish.
            </div>

            <div className="my-4">
              If you have any questions, click the chat button in the bottom left and we'd be happy to walk you through
              the process.
            </div>
          </div>
        </DrawerContent>
      </DrawerContainer>
    );
  }

  return (
    <DrawerContainer>
      <DrawerContent>
        <div className="">
          <div className="text-base">CivilGrid Records Research is included for free on your project! 🎉🎉🎉</div>

          <div className="my-4 text-sm">
            💡 The CivilGrid team will obtain Maps and As-Builts from agencies on your behalf. Here are a few steps you
            can take to ensure fast delivery of data BEFORE you submit requests.
          </div>

          <div className="my-8 flex flex-col space-y-4 text-sm">
            <div>
              <div className="font-medium">
                1. Project boundaries should be finalized to the best of your knowledge.
              </div>
              <div>
                Click{' '}
                <Link to="/projects/$projectId/$tabId" params={{ projectId, tabId: 'details' }}>
                  here
                </Link>{' '}
                to go to Project Info, and then click <span className="font-medium">Edit Project Boundary</span>.
                Include the full extent of all areas that you are interested in. If there is any uncertainty, it’s best
                practice to include those areas - it’s always easier to request more than less.
              </div>

              <div className="bg-brand-light p-4">
                ⚠️ If you make a project boundary edit after initiating requests, be aware that a{' '}
                {CHANGE_ORDER_FEE.toFormat()} Change Order Fee will be incurred on the project. This is to account for
                the extra work in re-requesting all of the agencies with the new boundaries.
              </div>
            </div>

            <div>
              <div className="font-medium">2. Complete your Project Narrative</div>
              <div>
                Click{' '}
                <Link to="/projects/$projectId/$tabId" params={{ projectId, tabId: 'details' }}>
                  here
                </Link>{' '}
                to go to Project Info and complete your Project Narrative. Many agencies ask for this information prior
                to releasing records, so filling it out significantly increases the likelihood of a successful request.
                The Project Narrative will be used by CivilGrid’s team in communication with requested agencies.
              </div>
            </div>

            <div>
              <div className="font-medium">3. Request both Maps and As-Builts at the same time</div>
              <div>
                If you think you’ll need as-builts later on in your process, go ahead and check those boxes. Agencies
                process a single, joint request more quickly than two separate requests.
              </div>
            </div>

            <div>
              <div className="font-medium">4. Obtain and complete forms/letters ahead of time</div>
              <div>
                Some agencies will waive map fees for projects done for a utility or public agency. A letter on your
                client agency’s letterhead is required for this. Known agency requirements and their forms/templates can
                be found in the next step.
              </div>
            </div>
          </div>

          <div className="text-sm">
            This{' '}
            <a
              href="https://civilgrid.help.usepylon.com/articles/1535603023-step-by-step-guide-to-using-request-data-for-records-research"
              target="_blank"
              rel="noreferrer noopener"
              className="text-blue-50 underline hover:text-blue-60"
            >
              support article
            </a>{' '}
            has more details on how to complete a Records Research request, and don’t hesitate to ask if you have any
            questions! Use the Chat (blue button, bottom-left) for general questions on the app or process, or use the
            Project Channel ( <Icon className="inline-flex" icon="comment" />  button right above this) for questions
            specific to this project.
          </div>
        </div>
      </DrawerContent>

      <DrawerFooter>
        <div className="invisible">
          <PreviousButton onClick={() => store.toggleFormStep(-1)} />
        </div>
        <NextButton onClick={() => store.toggleFormStep(1)} />
      </DrawerFooter>
    </DrawerContainer>
  );
}
